import React, { createContext, ReactNode, useContext, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import Dialog from './Dialog';

interface PopupEntry {
    element: HTMLElement;
    render: () => ReactNode;
    hovered: boolean;
}

function createPopupStore() {
    const entries = new Map<object, PopupEntry>();
    const listeners = new Set<() => void>();
    let active: PopupEntry | undefined;

    return {
        subscribe(listener: () => void) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
        getSnapshot: () => active,
        update(key: object, entry?: PopupEntry) {
            if (entry) entries.set(key, entry);
            else entries.delete(key);

            let next: PopupEntry | undefined;
            entries.forEach(item => {
                if (!next || item.hovered) next = item;
            });
            if (next !== active) {
                active = next;
                listeners.forEach(listener => listener());
            }
        }
    };
}

const MarkerPopupContext = createContext<ReturnType<typeof createPopupStore> | null>(null);
export const useMarkerPopup = () => useContext(MarkerPopupContext);

const Popup = ({ store }: { store: ReturnType<typeof createPopupStore> }) => {
    const active = useSyncExternalStore(store.subscribe, store.getSnapshot);
    return active ? createPortal(
        <Dialog visible>{active.render()}</Dialog>,
        active.element
    ) : null;
};

export const MarkerPopupProvider = ({ children }: { children: ReactNode }) => {
    const [store] = useState(createPopupStore);
    return <MarkerPopupContext.Provider value={store}>
        {children}
        <Popup store={store} />
    </MarkerPopupContext.Provider>;
};
